import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IonIcon } from "@ionic/react";
import { peopleOutline, timeOutline, sparklesOutline, globeOutline } from "ionicons/icons";
import All from "./components/All";
import { setCurrentTab } from "../../redux/slices/CurrentTab";
import { features, isEmbroideryTrace } from "../../config/appVariant";
import "./index.css";

import { Tabs } from "antd";
import MyFiles from "./components/FileList/MyFiles";
import Uploaded from "./components/FileList/Uploaded";
import Community from "./components/FileList/Community";
const { TabPane } = Tabs;

// "New" is only true until you've looked, so the badge retires itself on the
// first visit rather than sitting there claiming novelty months from now. Only
// EmbroideryTrace gets it — the tab has been part of SvgTrace for a long time.
const COMMUNITY_SEEN_KEY = "communityTabSeen";

const readCommunitySeen = () => {
  try {
    return localStorage.getItem(COMMUNITY_SEEN_KEY) === "1";
  } catch (_) {
    // Private browsing: skip the badge rather than show one that can never be
    // dismissed on every single visit.
    return true;
  }
};

const Colection = function ({ setRerender, rerender, setIsImageGeneratorModalVisible, onSvgFile, onAutoDigitizeFile, autoDigitizeBusy }) {
  const currentTab = useSelector((state) => state.currentTab.tab);
  const dispatch = useDispatch();
  const tabsContainerRef = useRef(null);

  const [activeTab, setActiveTab] = useState(currentTab);

  const [showCommunityBadge, setShowCommunityBadge] = useState(
    () => isEmbroideryTrace && !readCommunitySeen()
  );

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    dispatch(setCurrentTab(key));
    // Both the click and the touch handler come through here, so retiring the
    // badge in one place covers every way into the tab.
    if (key === "community") {
      setShowCommunityBadge(false);
      try { localStorage.setItem(COMMUNITY_SEEN_KEY, "1"); } catch (_) {}
    }
  }, [dispatch]);

  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const handleTouchEnd = (e) => {
      const tab = e.target.closest('.ant-tabs-tab');
      if (!tab) return;

      e.preventDefault();

      const tabs = container.querySelectorAll('.ant-tabs-tab');
      const tabIndex = Array.from(tabs).indexOf(tab);
      const tabKeys = ['carousel', 'community'];
      if (tabIndex >= 0 && tabIndex < tabKeys.length) {
        handleTabChange(tabKeys[tabIndex]);
      }
    };

    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => container.removeEventListener('touchend', handleTouchEnd);
  }, [handleTabChange]);

  // When community is hidden (EmbroideryTrace), skip the tab bar entirely
  if (!features.community) {
    return (
      <All
        setRerender={setRerender}
        rerender={rerender}
        setIsImageGeneratorModalVisible={setIsImageGeneratorModalVisible}
        onSvgFile={onSvgFile}
        onAutoDigitizeFile={onAutoDigitizeFile}
        autoDigitizeBusy={autoDigitizeBusy}
      />
    );
  }

  return (
    <div className="professional-tabs-container" ref={tabsContainerRef}>
    <Tabs
      activeKey={activeTab}
      onChange={handleTabChange}
        className="professional-tabs"
        size="large"
    >
      <TabPane 
        tab={
            <div className="professional-tab-content">
              <IonIcon icon={timeOutline} className="tab-icon" />
              <span className="tab-label">Recent Files</span>
            </div>
        } 
        key="carousel"
      >
        <All 
          setRerender={setRerender} 
          rerender={rerender}
          setIsImageGeneratorModalVisible={setIsImageGeneratorModalVisible}
          onSvgFile={onSvgFile}
          onAutoDigitizeFile={onAutoDigitizeFile}
          autoDigitizeBusy={autoDigitizeBusy}
        />
      </TabPane>
      <TabPane 
        tab={
            <div className="professional-tab-content community-tab">
              <IonIcon icon={peopleOutline} className="tab-icon" />
              {/* "Community" alone doesn't say whether it's a forum, a chat or a
                  gallery. Naming the thing it holds — and matching the noun
                  embroiderers already use — makes it the sibling of "Recent
                  Files": whose, and what. */}
              <span className="tab-label community-label">
                Community
                {isEmbroideryTrace && (
                  <span className="tab-label-suffix"> Designs</span>
                )}
              </span>
              {showCommunityBadge && (
                <span className="tab-badge-new">New</span>
              )}
            </div>
        } 
        key="community"
      >
        <Community />
      </TabPane>
    </Tabs>
    </div>
  );
};

export default Colection;
